import { api } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { StageOption } from '@/features/auth/types'
import {
  DEMO_STAGES,
  demoSave,
  demoSaveStageNames,
  demoSetLogo,
  demoSettings,
  demoStageNames,
} from '@/features/settings/demo/demoCompanySettings'
import type { CompanySettings, CompanySettingsInput, StageName } from '@/features/settings/types'
import { env } from '@/lib/env'
import { TERM_KEYS, type TermKey, type Workspace } from '@/types/workspace'

/** `SettingsView.java`. */
interface SettingsViewResponse {
  effective: Workspace
  displayName: { value: string | null; defaultValue: string }
  brandColor: { value: string | null; defaultValue: string }
  terms: Record<
    TermKey,
    {
      singular: string | null
      plural: string | null
      defaultSingular: string
      defaultPlural: string
    }
  >
}

/** `SettingsRequest.java`. A blank or absent value resets that field. */
interface SettingsRequest {
  displayName: string
  brandColor: string
  terms: Partial<Record<TermKey, { singular: string; plural: string }>>
}

/** `StageLabelField.java`. */
interface StageLabelFieldResponse {
  stage: string
  stageSection: string
  name: string | null
  defaultName: string
}

export function toCompanySettings(response: SettingsViewResponse): CompanySettings {
  return { ...response }
}

export function toSettingsRequest(input: CompanySettingsInput): SettingsRequest {
  const terms: SettingsRequest['terms'] = {}
  for (const key of TERM_KEYS) {
    const singular = input.terms[key].singular.trim()
    const plural = input.terms[key].plural.trim()
    if (singular && plural) terms[key] = { singular, plural }
  }
  return {
    displayName: input.displayName.trim(),
    brandColor: input.brandColor.trim().toLowerCase(),
    terms,
  }
}

export function toStageNames(response: StageLabelFieldResponse[]): StageName[] {
  return response.map((row) => ({
    stage: row.stage,
    section: row.stageSection,
    name: row.name,
    defaultName: row.defaultName,
  }))
}

/** The session's board columns, renamed as the server now names them. */
export function applyStageNames(stages: StageOption[], names: StageName[]): StageOption[] {
  const byStage = new Map(names.map((row) => [row.stage, row.name ?? row.defaultName]))
  return stages.map((option) => ({
    ...option,
    stageLabel: byStage.get(option.stage) ?? option.stageLabel,
  }))
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const companySettingsApi = {
  fetch: async (): Promise<CompanySettings> => {
    if (env.demoMode) {
      await wait(150)
      return demoSettings()
    }
    return toCompanySettings(await api.get<SettingsViewResponse>(endpoints.workspace.settings))
  },

  save: async (input: CompanySettingsInput): Promise<CompanySettings> => {
    if (env.demoMode) {
      await wait(250)
      return demoSave(toSettingsRequest(input))
    }
    return toCompanySettings(
      await api.put<SettingsViewResponse>(endpoints.workspace.settings, toSettingsRequest(input)),
    )
  },

  uploadLogo: async (file: Blob): Promise<CompanySettings> => {
    if (env.demoMode) {
      await wait(250)
      return demoSetLogo(file)
    }
    return toCompanySettings(
      await api.putBinary<SettingsViewResponse>(endpoints.workspace.logo, file),
    )
  },

  removeLogo: async (): Promise<CompanySettings> => {
    if (env.demoMode) {
      await wait(150)
      return demoSetLogo(null)
    }
    return toCompanySettings(await api.delete<SettingsViewResponse>(endpoints.workspace.logo))
  },

  fetchStageNames: async (): Promise<StageName[]> => {
    if (env.demoMode) {
      await wait(150)
      return demoStageNames(DEMO_STAGES)
    }
    return toStageNames(await api.get<StageLabelFieldResponse[]>(endpoints.workspace.stageLabels))
  },

  saveStageNames: async (names: Record<string, string>): Promise<StageName[]> => {
    if (env.demoMode) {
      await wait(250)
      demoSaveStageNames(names)
      return demoStageNames(DEMO_STAGES)
    }
    return toStageNames(
      await api.put<StageLabelFieldResponse[]>(endpoints.workspace.stageLabels, names),
    )
  },
}
